/**
 * ScannedVehicleCard — le conducteur a scanné le QR pare-brise.
 *
 * Le serveur décide (`poolScanAPI.resolve`, `backend/modules/pool/scan_service.mjs`) : quel
 * véhicule, et quel geste lui revient maintenant — rendre, prendre, attendre ou réserver.
 * Cette carte ne fait que présenter ce geste et le déléguer à l'écran conducteur, qui garde
 * seul la prise, la restitution et la réservation (mêmes refus, même contrat).
 *
 * Elle le dit aussi : scanner n'ouvre pas la voiture.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { QrCode, X, AlertTriangle } from 'lucide-react';
import { poolScanAPI } from '../../../api/pool.js';

function ScannedVehicleCard({ token, canCheckout, canReturn, canBook, booking, onBook, onCheckout, onReturn, onDismiss, t, fmt }) {
  const scan = useQuery({
    queryKey: ['pool-scan', token],
    queryFn: () => poolScanAPI.resolve(token),
    enabled: Boolean(token),
    retry: false,
  });
  const data = scan.data?.data || scan.data || null;

  const button = 'min-h-[44px] w-full rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60';

  return (
    <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4" role="region" aria-label={t('poolScan.card.title')}>
      <div className="flex items-start justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
          <QrCode className="h-4 w-4" /> {t('poolScan.card.title')}
        </p>
        <button type="button" onClick={onDismiss} className="text-emerald-700 hover:text-emerald-900" aria-label={t('poolScan.card.dismiss')}>
          <X className="h-4 w-4" />
        </button>
      </div>

      {scan.isLoading && <p className="mt-2 text-sm text-emerald-800">{t('poolScan.card.loading')}</p>}

      {scan.isError && (
        <p className="mt-2 flex items-start gap-1.5 text-sm text-red-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {t('poolScan.card.unknown')}
        </p>
      )}

      {data && (
        <div className="mt-2 space-y-2">
          <p className="text-xl font-bold text-gray-900">{data.license_plate}</p>
          <p className="text-sm text-gray-700">{[data.make, data.model].filter(Boolean).join(' ')}</p>

          {data.next_action === 'return' && (
            canReturn
              ? <button type="button" className={button} onClick={onReturn}>{t('poolScan.card.return')}</button>
              : <p className="text-sm text-gray-700">{t('poolScan.card.returnElsewhere')}</p>
          )}
          {data.next_action === 'checkout' && (
            canCheckout
              ? <button type="button" className={button} onClick={onCheckout}>{t('poolScan.card.checkout')}</button>
              : <p className="text-sm text-gray-700">{t('poolScan.card.checkoutElsewhere')}</p>
          )}
          {data.next_action === 'wait' && data.reservation && (
            <p className="text-sm text-gray-700">
              {t('poolScan.card.wait', { from: fmt.dateTime(data.reservation.planned_start) })}
            </p>
          )}
          {data.next_action === 'book' && (
            canBook
              ? (
                <button type="button" className={button} disabled={booking} onClick={() => onBook(data.vehicle_id)}>
                  {booking ? t('poolDriver.search.booking') : t('poolScan.card.book')}
                </button>
              )
              : <p className="text-sm text-gray-700">{t('poolDriver.picker.bookDisabledHint')}</p>
          )}

          <p className="text-xs text-emerald-800">{t('poolScan.card.noUnlock')}</p>
        </div>
      )}
    </div>
  );
}

export default ScannedVehicleCard;
